import {UndoOutlined} from '@ant-design/icons';
import {Button, message} from 'antd';
import Spin from 'antd/es/spin';
import cn from 'classnames';
import React, {
    useContext, useEffect, useRef, useState,
} from 'react';
import {v4 as uid} from 'uuid';

import {IconsMap} from 'components/dynamic-icon';
import {
    TPredicateExpressionCreatorRef,
    TPredicateExpressionGroupTuple,
    PredicateExpressionFormListContext,
    transformExpressionArrayToFilterExpressionDtoFunction,
} from 'features/predicate-expression-form-list';
import {ReactComponent as ArrowBackOutlined} from 'shared/assets/arrow-back-outlined.svg';
import {showMessage} from 'shared/utils';
import {useQueryParams} from 'shared/utils/query-params';
import {showRequestErrorMessage} from 'shared/utils/show-message-from-response';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {selectIsThunkPending} from 'store/slices/loading-state-slice';
import {loadDrilldownParameters} from 'store/slices/report-configuration-slice/dd-parameters-configuration/dd-parameters-configuration-thunks';
import {
    loadDdRestrictions,
    updateDdRestrictions,
} from 'store/slices/report-configuration-slice/dd-restrictions-configuration';
import {
    ddReferenceRulesSelectors,
    reportConfigurationActions,
    selectDrilldownRestrictionsExpression,
} from 'store/slices/report-configuration-slice/report-configuration-slice';

import {ButtonChangeHistory} from '../../components/button-change-history';
import {DrillDownRuleInfo} from '../../components/drill-down-rule-info';
import {DRILLDOWN_ID_PARAM_KEY, EReportConfigurationTabKey} from '../../report-configuration.constants';
import {ReportConfigurationContext} from '../../report-configuration.context';
import {DdRestrictionExpressionCreator} from './dd-restriction-expression-creator';

export const ReportDdRestrictions = () => {
    const dispatch = useAppDispatch();
    const {query, removeQueryParams} = useQueryParams();

    const {setSelectedTabKey, templateCode} = useContext(ReportConfigurationContext);

    const [isEditing, setEditing] = useState(false);

    const restrictionExpressionCreatorRef = useRef<TPredicateExpressionCreatorRef>(null);
    const [restrictionInitialExpression, setRestrictionInitialExpression] = useState<TPredicateExpressionGroupTuple>([{
        connectBy: 'OR',
        enabled: true,
        uid: uid(),
        ordinal: 0,
    }]);

    const drillDownId = Number(query.get(DRILLDOWN_ID_PARAM_KEY));
    const drillDown = useAppSelector(state => ddReferenceRulesSelectors.selectById(state, drillDownId));

    const ddRestrictionsExpression = useAppSelector(selectDrilldownRestrictionsExpression);

    const isUpdatingDdRestrictions = useAppSelector(s => selectIsThunkPending(s, updateDdRestrictions.typePrefix));
    const [isLoadingDdRestrictions, setIsLoadingDdRestrictions] = useState(false);

    useEffect(() => {
        if (!ddRestrictionsExpression) {
            setIsLoadingDdRestrictions(true);

            dispatch(loadDrilldownParameters({
                drillId: `${drillDownId}`,
            }));

            const loadDrilldownRestrictionThunk = dispatch(loadDdRestrictions({
                drillId: drillDownId,
                templateCode,
            }));

            loadDrilldownRestrictionThunk.unwrap().then(() => {}, () => {}).finally(() => {
                setIsLoadingDdRestrictions(false);
            });
        }
    }, []);

    useEffect(() => {
        if (ddRestrictionsExpression) {
            setRestrictionInitialExpression(ddRestrictionsExpression);
        }
    }, [ddRestrictionsExpression]);

    const handleSave = async () => {
        try {
            const valuesToSave = await restrictionExpressionCreatorRef.current?.getValuesToSave();
            setEditing(false);
            if (valuesToSave) {
                try {
                    const restrictionExpressionDtoToSave = transformExpressionArrayToFilterExpressionDtoFunction(
                        valuesToSave,
                    );

                    await dispatch(updateDdRestrictions({
                        drillId: drillDownId,
                        restrictions: restrictionExpressionDtoToSave,
                    })).unwrap();

                    showMessage({
                        message: 'Правила фильтрации успешно обновлены',
                    });
                } catch (e) {
                    showRequestErrorMessage(e);
                }
            }
        } catch (e) {
            message.error({
                content: 'Пожалуйста, проверьте корректность ввода данных фильтрации',
                duration: 2,
            });
        }
    };

    const handleCancel = () => {
        setEditing(false);
        setRestrictionInitialExpression([...restrictionInitialExpression]);
    };

    return (
        <PredicateExpressionFormListContext.Provider
            value={{
                templateCode,
                initialExpression: restrictionInitialExpression,
            }}
        >
            <div className={cn('report-dd-filters')}>

                {drillDown && (
                    <div className={cn('report-dd-filters__dd-rule')}>
                        <div className={cn('report-dd-filters__dd-rule__label')}>
                            Правило связи:
                        </div>
                        <DrillDownRuleInfo drillDownRule={drillDown} />
                    </div>
                )}

                <div className={cn('report-dd-filters__upbar', 'report-dd-filters__buttons-bar')}>
                    {!isEditing && (
                        <Button
                            disabled={isLoadingDdRestrictions || isUpdatingDdRestrictions}
                            type="primary"
                            onClick={() => setEditing(true)}
                        >
                            <IconsMap.EditWithLine />
                            Редактировать
                        </Button>
                    )}
                    {isEditing && (
                        <Button
                            type="primary"
                            onClick={handleSave}
                        >
                            <IconsMap.SaveOutlined />
                            Сохранить
                        </Button>
                    )}
                    {isEditing && (
                        <Button
                            type="default"
                            onClick={handleCancel}
                        >
                            <UndoOutlined />
                            Отменить
                        </Button>
                    )}
                    <ButtonChangeHistory />
                    <Button
                        type="default"
                        icon={<ArrowBackOutlined />}
                        onClick={() => {
                            removeQueryParams(['drillDownId'], {action: 'replace'});
                            dispatch(reportConfigurationActions.purgeDrilldownRestrictions());
                            setSelectedTabKey?.(EReportConfigurationTabKey.REPORT_DD_REFERENCES);
                        }}
                    >
                        Вернуться на вкладку «Настройка правил связей документов (ДД)»
                    </Button>
                </div>

                <div className={cn('report-dd-filters__section-title')} />

                <div className={cn('report-dd-filters__body')}>
                    <Spin
                        tip={isUpdatingDdRestrictions ? 'Обновление правил...' : 'Загрузка...'}
                        spinning={isLoadingDdRestrictions || isUpdatingDdRestrictions}
                    >
                        <DdRestrictionExpressionCreator
                            ref={restrictionExpressionCreatorRef}
                            initialExpression={restrictionInitialExpression}
                            isEditing={isEditing}
                        />
                    </Spin>
                </div>
            </div>
        </PredicateExpressionFormListContext.Provider>
    );
};
